import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MDRBadge } from "@/components/MDRBadge";
import {
  pharmaciesData, accreditedStructuresData, medicalDeviceModelsData, medicalDeviceKitsData,
  managersData, diagnosticRoomsData
} from "@/data/platformData";
import { userRegistryData, userRoles } from "@/data/usersRegistry";
import {
  Building2, Hospital, Cpu, Users, Settings, Layers, Globe, ShieldCheck,
  UserCog, FileDown, UserPlus, ArrowRight, Stethoscope, ShieldUser, Pill, BriefcaseMedical
} from "lucide-react";

const roleIconMap = {
  admin: ShieldUser,
  operator: Pill,
  doctor: Stethoscope,
  pharmacy_manager: BriefcaseMedical,
  structure_manager: Hospital
};

export const AdminHomePage = () => {
  const regions = [...new Set(pharmaciesData.map(p => p.region))];

  // User population stats
  const totalUsers = userRegistryData.length;
  const importedUsers = userRegistryData.filter(u => u.source === "regional_registry").length;
  const manualUsers = totalUsers - importedUsers;
  const importedPct = totalUsers > 0 ? Math.round((importedUsers / totalUsers) * 100) : 0;
  const usersByRole = userRoles.map(r => ({
    ...r,
    count: userRegistryData.filter(u => u.roles.includes(r.id)).length
  }));

  const kpis = [
    { label: "Farmacie", value: pharmaciesData.length, icon: Building2, accent: "from-indigo-500/10 to-indigo-500/5 text-indigo-700" },
    { label: "Strutture Accreditate", value: accreditedStructuresData.length, icon: Hospital, accent: "from-cyan-500/10 to-cyan-500/5 text-cyan-700" },
    { label: "Sale diagnostiche", value: diagnosticRoomsData.length, icon: Layers, accent: "from-sky-500/10 to-sky-500/5 text-sky-700" },
    { label: "Modelli elettromedicali", value: medicalDeviceModelsData.length, icon: Cpu, accent: "from-rose-500/10 to-rose-500/5 text-rose-700" }
  ];

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Settings className="h-4 w-4 text-slate-600" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Amministratore di Sistema</span>
          <MDRBadge variant="admin" size="sm" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Plancia di controllo</h1>
        <p className="text-sm text-muted-foreground mt-1">Configurazione anagrafiche e monitoraggio piattaforma multi-tenant</p>
      </div>

      {/* KPI */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className={`bg-gradient-to-br ${k.accent} border-2`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/60 dark:bg-black/20 flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold leading-none">{k.value}</p>
                  <p className="text-xs mt-1 opacity-80">{k.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Popolazione utenti — sintesi rapida del censimento */}
      <Card data-testid="admin-home-users-overview" className="border-2 border-violet-200/60 bg-gradient-to-br from-violet-50/40 via-white to-indigo-50/30">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <UserCog className="h-4 w-4 text-violet-700" /> Popolazione utenti piattaforma
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Sintesi del censimento globale · {userRoles.length} profili supportati
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="outline" data-testid="admin-home-users-import-btn">
                <Link to="/admin/config/users?action=import">
                  <FileDown className="h-3.5 w-3.5 mr-1.5" /> Importa albo
                </Link>
              </Button>
              <Button asChild size="sm" data-testid="admin-home-users-new-btn">
                <Link to="/admin/config/users?action=new">
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Nuovo utente
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Totali */}
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <div data-testid="admin-home-users-stat-total" className="rounded-xl border bg-white p-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-violet-700" />
              </div>
              <div>
                <p className="text-2xl font-bold leading-none">{totalUsers}</p>
                <p className="text-[11px] text-muted-foreground mt-1 uppercase tracking-wider">Utenti totali</p>
              </div>
            </div>
            <div data-testid="admin-home-users-stat-imported" className="rounded-xl border bg-white p-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <FileDown className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold leading-none">{importedUsers}</p>
                <p className="text-[11px] text-muted-foreground mt-1 uppercase tracking-wider">Da albo regionale</p>
              </div>
            </div>
            <div data-testid="admin-home-users-stat-manual" className="rounded-xl border bg-white p-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-sky-700" />
              </div>
              <div>
                <p className="text-2xl font-bold leading-none">{manualUsers}</p>
                <p className="text-[11px] text-muted-foreground mt-1 uppercase tracking-wider">Inseriti manualmente</p>
              </div>
            </div>
            <div data-testid="admin-home-users-stat-coverage" className="rounded-xl border bg-white p-3">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Copertura albo</p>
                <span className="text-sm font-bold text-emerald-700">{importedPct}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all" style={{ width: `${importedPct}%` }} />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">Utenti verificati su fonti regionali</p>
            </div>
          </div>

          {/* Distribuzione per ruolo */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Distribuzione per profilo</p>
              <Link to="/admin/config/users" className="text-xs text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="admin-home-users-registry-link">
                Apri censimento <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid gap-2 grid-cols-2 lg:grid-cols-5">
              {usersByRole.map(r => {
                const Icon = roleIconMap[r.id] || Users;
                const pct = totalUsers > 0 ? Math.round((r.count / totalUsers) * 100) : 0;
                return (
                  <div
                    key={r.id}
                    data-testid={`admin-home-users-role-${r.id}`}
                    className={`rounded-lg border p-3 ${r.color}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className="h-4 w-4" />
                      <span className="text-[11px] font-semibold uppercase tracking-wider truncate">{r.label}</span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-bold leading-none">{r.count}</span>
                      <span className="text-xs opacity-70">· {pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Regions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-4 w-4 text-indigo-600" /> Distribuzione regionale
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {regions.map(r => {
              const count = pharmaciesData.filter(p => p.region === r).length;
              return (
                <div key={r} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="font-medium text-sm">{r}</span>
                  <Badge variant="secondary">{count} farmacie</Badge>
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground pt-1">
              La piattaforma è pronta per integrazione con Anagrafe Sanitaria Regionale (ASR) di ciascuna regione.
            </p>
          </CardContent>
        </Card>

        {/* Device kits */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Cpu className="h-4 w-4 text-rose-600" /> Kit elettromedicali
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {medicalDeviceKitsData.map(k => (
              <div key={k.id} className="p-3 rounded-lg border bg-card">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm">{k.name}</p>
                  <Badge variant="outline">{k.deviceModelIds.length} modelli</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{k.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Managers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-600" /> Responsabili censiti
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {managersData.map(m => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div>
                  <p className="font-medium text-sm">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.email}</p>
                </div>
                <Badge variant={m.role === "structure_manager" ? "outline" : "secondary"} className="text-[10px] uppercase">
                  {m.role === "structure_manager" ? "Struttura" : "Farmacia"}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Compliance */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> Conformità e certificazioni
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between p-2 rounded border-l-2 border-emerald-500 bg-emerald-50/50">
              <span>GDPR · Reg. UE 2016/679</span>
              <Badge className="bg-emerald-600 hover:bg-emerald-600">Attivo</Badge>
            </div>
            <div className="flex items-center justify-between p-2 rounded border-l-2 border-emerald-500 bg-emerald-50/50">
              <span>NIS2 · D.Lgs. 138/2024</span>
              <Badge className="bg-emerald-600 hover:bg-emerald-600">Attivo</Badge>
            </div>
            <div className="flex items-center justify-between p-2 rounded border-l-2 border-rose-500 bg-rose-50/50">
              <span>MDR Classe IIa (componenti clinici)</span>
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">In certificazione</Badge>
            </div>
            <div className="flex items-center justify-between p-2 rounded border-l-2 border-amber-500 bg-amber-50/50">
              <span>ISO 13485 · Sistema qualità</span>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">In allestimento</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-dashed border-2 bg-muted/30">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-sm">Roadmap modulo Amministratore</p>
            <p className="text-xs text-muted-foreground">
              CRUD anagrafiche regionali, censimento responsabili, aggregazione kit elettromedicali, rendicontazione globale.
            </p>
          </div>
          <Badge variant="outline">Fase 7</Badge>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHomePage;
